export const formatHomeHealthTemplateDisplayName = (
  templateName: string | null | undefined
): string => {
  const name = templateName?.trim() ?? "";
  if (!name) return "";

  return name
    .replace(/^Peds NP(?=\s*-)/i, "Peds Provider")
    .replace(/^NP(?=\s*-)/i, "Provider");
};

type HomeHealthTemplateDisciplineKey =
  | "provider"
  | "rn"
  | "sw"
  | "chap"
  | "oasis"
  | "other";

const HOME_HEALTH_TEMPLATE_DISCIPLINES: Array<{
  key: HomeHealthTemplateDisciplineKey;
  label: string;
  rank: number;
}> = [
  { key: "provider", label: "Provider", rank: 0 },
  { key: "rn", label: "RN", rank: 1 },
  { key: "sw", label: "SW", rank: 2 },
  { key: "chap", label: "Chaplain", rank: 3 },
  { key: "oasis", label: "OASIS", rank: 4 },
  { key: "other", label: "Other", rank: 99 },
];

const getHomeHealthTemplateDiscipline = (templateName: string) => {
  const displayName = formatHomeHealthTemplateDisplayName(templateName);
  const normalized = displayName.toLowerCase();

  if (normalized.includes("provider")) {
    return HOME_HEALTH_TEMPLATE_DISCIPLINES[0];
  }
  if (normalized.includes("rn")) {
    return HOME_HEALTH_TEMPLATE_DISCIPLINES[1];
  }
  if (normalized.includes("sw")) {
    return HOME_HEALTH_TEMPLATE_DISCIPLINES[2];
  }
  if (normalized.startsWith("chap")) {
    return HOME_HEALTH_TEMPLATE_DISCIPLINES[3];
  }
  if (normalized.startsWith("oasis")) {
    return HOME_HEALTH_TEMPLATE_DISCIPLINES[4];
  }
  return HOME_HEALTH_TEMPLATE_DISCIPLINES[5];
};

const getHomeHealthTemplateSortKey = (templateName: string) => {
  const displayName = formatHomeHealthTemplateDisplayName(templateName);
  const normalized = displayName.toLowerCase();
  const isPeds = normalized.startsWith("peds ");
  const discipline = getHomeHealthTemplateDiscipline(templateName);

  const visitRank = normalized.includes("initial")
    ? 0
    : normalized.includes("follow")
    ? 1
    : 2;

  return {
    roleRank: discipline.rank,
    pedsRank: isPeds ? 1 : 0,
    visitRank,
    noPeRank: normalized.includes("no pe") ? 1 : 0,
    displayName,
  };
};

export const sortHomeHealthTemplates = <
  T extends { name?: string | null | undefined }
>(
  templates: T[]
): T[] => {
  return [...templates].sort((a, b) => {
    const sortA = getHomeHealthTemplateSortKey(a.name ?? "");
    const sortB = getHomeHealthTemplateSortKey(b.name ?? "");

    return (
      sortA.roleRank - sortB.roleRank ||
      sortA.pedsRank - sortB.pedsRank ||
      sortA.visitRank - sortB.visitRank ||
      sortA.noPeRank - sortB.noPeRank ||
      sortA.displayName.localeCompare(sortB.displayName)
    );
  });
};

export const groupHomeHealthTemplatesByDiscipline = <
  T extends { name?: string | null | undefined }
>(
  templates: T[]
): Array<{
  key: HomeHealthTemplateDisciplineKey;
  label: string;
  templates: T[];
}> => {
  const groups = new Map<
    HomeHealthTemplateDisciplineKey,
    { key: HomeHealthTemplateDisciplineKey; label: string; templates: T[] }
  >();

  sortHomeHealthTemplates(templates).forEach((template) => {
    const discipline = getHomeHealthTemplateDiscipline(template.name ?? "");
    const existing = groups.get(discipline.key) ?? {
      key: discipline.key,
      label: discipline.label,
      templates: [],
    };
    existing.templates.push(template);
    groups.set(discipline.key, existing);
  });

  return Array.from(groups.values());
};
